import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const BOOKS = "yh-offline-books";
const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "caches" in window;

export const registerOffline = () => {
  if (!supported()) return;
  window.addEventListener("load", () => { navigator.serviceWorker.register("/sw.js").catch(() => {}); });
};

const base = () => api.defaults.baseURL;
const bookUrls = (b) => [
  `${base()}/books/${b.id}`,
  `${base()}/books/${b.id}/file`,
  `${base()}/books/${b.id}/cover?v=2`,
  ...Array.from({ length: b.page_count || 0 }, (_, p) => `${base()}/tts/${b.id}/${p}/audio`),
];

const store = async (cache, url) => {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(String(res.status));
  await cache.put(url, res);
};

// Saves each book (file, cover, details and the audio of every page) on this device.
export const saveBooksOffline = async (books, onProgress) => {
  if (!supported()) throw new Error("unsupported");
  const cache = await caches.open(BOOKS);
  const jobs = books.flatMap((b) => bookUrls(b).map((url, i) => ({ url, core: i < 2 })));
  let done = 0;
  for (const j of jobs) {
    if (!(await cache.match(j.url, { ignoreVary: true }))) {
      // Audio that fails (e.g. a page with no text) should not stop the book from being saved.
      await store(cache, j.url).catch((e) => { if (j.core) throw e; });
    }
    done += 1;
    onProgress?.(Math.round((done / jobs.length) * 100));
  }
};

export const savedBookIds = async () => {
  if (!supported()) return new Set();
  const keys = await (await caches.open(BOOKS)).keys();
  return new Set(keys.map((r) => r.url.match(/\/books\/([^/?]+)\/file$/)?.[1]).filter(Boolean));
};

export const useOnline = () => {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
};
